import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Tag, Button, Typography, Space, message, Modal, Select, InputNumber, Statistic } from 'antd';
import { KeyOutlined, ReloadOutlined, GoldOutlined } from '@ant-design/icons';
import { activationAPI, coinAPI, subscriptionAPI } from '../services/api';
import ActivationModal from '../components/ActivationModal';

const { Title, Text } = Typography;

interface ActivationRecord {
  id: string;
  code: string;
  plan_name: string;
  duration_days: number;
  status: string;
  used_at: string | null;
}

interface ExchangePlan {
  name: string;
  display_name?: string;
  price_monthly?: number | string | null;
}

const monthlyCoinPrice = (plan?: ExchangePlan): number | null => {
  const price = Number(plan?.price_monthly);
  return Number.isFinite(price) && price > 0 ? price : null;
};

export const getExchangeablePlans = (plans: ExchangePlan[]): ExchangePlan[] =>
  plans.filter((plan) => plan.name !== 'free' && monthlyCoinPrice(plan) !== null);

export const calculateExchangeCost = (plan: ExchangePlan | undefined, durationDays: number): number | null => {
  const price = monthlyCoinPrice(plan);
  if (price === null || !Number.isFinite(durationDays) || durationDays < 1) return null;
  return Math.ceil(price * durationDays / 30);
};

const PLAN_LABELS: Record<string, string> = {
  free: '免费版',
  basic: '基础版',
  professional: '专业版',
  enterprise: '企业版',
};

const STATUS_MAP: Record<string, { color: string; label: string }> = {
  used: { color: 'green', label: '已使用' },
  unused: { color: 'blue', label: '未使用' },
  expired: { color: 'default', label: '已过期' },
  revoked: { color: 'red', label: '已撤销' },
};

const ActivationManagement: React.FC = () => {
  const [records, setRecords] = useState<ActivationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [exchangeLoading, setExchangeLoading] = useState(false);
  const [coinBalance, setCoinBalance] = useState(0);
  const [plans, setPlans] = useState<ExchangePlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [duration, setDuration] = useState<number>(30);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await activationAPI.history();
      setRecords(data || []);
    } catch {
      message.error('获取激活记录失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const openExchange = async () => {
    setExchangeOpen(true);
    try {
      const [coinData, planData] = await Promise.all([
        coinAPI.getBalance(),
        subscriptionAPI.getPlans(),
      ]);
      setCoinBalance(coinData.balance || 0);
      const validPlans = getExchangeablePlans(planData || []);
      setPlans(validPlans);
      setSelectedPlan((current) => validPlans.some((plan) => plan.name === current) ? current : '');
      if (validPlans.length === 0) message.info('暂无可兑换的套餐');
    } catch (err) { console.error('openExchange failed', err); message.error('加载套餐信息失败'); }
  };

  const selectedPlanObj = plans.find((plan) => plan.name === selectedPlan);
  const exchangeCost = calculateExchangeCost(selectedPlanObj, duration);

  const handleExchange = async () => {
    if (!selectedPlan) { message.warning('请选择套餐'); return; }
    if (exchangeCost === null) { message.error('该套餐暂不支持金币兑换'); return; }
    if (exchangeCost > coinBalance) { message.error('金币余额不足'); return; }
    setExchangeLoading(true);
    try {
      const result = await coinAPI.exchange(selectedPlan, duration);
      message.success(`兑换成功！激活码: ${result.code}`);
      Modal.success({
        title: '兑换成功',
        content: (
          <div>
            <p>激活码: <Typography.Text copyable strong style={{ fontFamily: 'monospace' }}>{result.code}</Typography.Text></p>
            <p>套餐: {result.plan_display_name} / {result.duration_days}天</p>
            <p>消耗: {result.cost} 金币，剩余: {result.balance_after} 金币</p>
          </div>
        ),
      });
      setExchangeOpen(false);
      fetchHistory();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '兑换失败');
    } finally {
      setExchangeLoading(false);
    }
  };

  const columns = [
    {
      title: '激活码', dataIndex: 'code', key: 'code',
      render: (v: string) => <Text copyable style={{ fontFamily: 'monospace' }}>{v}</Text>,
    },
    {
      title: '套餐', dataIndex: 'plan_name', key: 'plan_name',
      render: (v: string) => PLAN_LABELS[v] || v,
    },
    {
      title: '时长(天)', dataIndex: 'duration_days', key: 'duration_days',
    },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: (v: string) => {
        const s = STATUS_MAP[v] || { color: 'default', label: v };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: '使用时间', dataIndex: 'used_at', key: 'used_at',
      render: (v: string | null) => v ? v.slice(0, 19).replace('T', ' ') : '-',
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>激活码管理</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchHistory}>刷新</Button>
          <Button icon={<GoldOutlined />} onClick={openExchange}>金币兑换激活码</Button>
          <Button type="primary" icon={<KeyOutlined />} onClick={() => setModalOpen(true)}>兑换激活码</Button>
        </Space>
      </div>

      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={records}
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: '暂无激活记录' }}
        />
      </Card>

      <ActivationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={fetchHistory}
      />

      <Modal
        title="金币兑换激活码"
        open={exchangeOpen}
        onCancel={() => setExchangeOpen(false)}
        onOk={handleExchange}
        confirmLoading={exchangeLoading}
        okText="确认兑换"
        okButtonProps={{ disabled: !selectedPlan || exchangeCost === null || exchangeCost > coinBalance }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Statistic title="当前金币余额" value={coinBalance} suffix="金币" />
          <div>
            <div style={{ marginBottom: 4 }}>选择套餐</div>
            <Select
              style={{ width: '100%' }}
              placeholder="选择要兑换的套餐"
              value={selectedPlan || undefined}
              onChange={setSelectedPlan}
              options={plans.map((plan) => ({ label: `${plan.display_name || plan.name} (${plan.price_monthly}金币/月)`, value: plan.name }))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>兑换时长</div>
            <Select
              style={{ width: '100%' }}
              value={duration}
              onChange={setDuration}
              options={[
                { label: '30天', value: 30 },
                { label: '90天', value: 90 },
                { label: '180天', value: 180 },
                { label: '365天', value: 365 },
              ]}
            />
          </div>
          {selectedPlan && exchangeCost !== null && (
            <Card size="small">
              <Statistic
                title="所需金币"
                value={exchangeCost}
                suffix="金币"
                valueStyle={{ color: exchangeCost > coinBalance ? '#ff4d4f' : '#52c41a' }}
              />
              {exchangeCost > coinBalance && (
                <Typography.Text type="danger" style={{ fontSize: 12 }}>余额不足，还差 {exchangeCost - coinBalance} 金币</Typography.Text>
              )}
            </Card>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default ActivationManagement;
